import {StoreApi, createStore, useStore} from 'zustand';
import {devtools} from 'zustand/middleware';
import produce from 'immer';
import {
  AdditionalFieldMapping,
  isAdditionalFieldMapping,
  SyncSettings,
  ManagedFieldMapping,
  isManagedFieldMapping,
  FieldMapping,
  IntegrationConfig,
} from '../../shared/integrations/crm';
import {useUpdateIntegration} from '../../hooks/api/integrations';
import {useEffect} from 'react';
import _ from 'lodash';
import {useMutation} from '@tanstack/react-query';
import {useToast} from '@chakra-ui/react';
import {useCrmIntegrationFromContext} from './IntegrationDetailsContext';
import {ObjectId} from 'bson';
import {z} from 'zod';
import {ObjectIdSchema} from '../../shared/zod';
import {zodTypeguard} from '../../shared/api/helpers';
import {CrmIntegrationResponse} from '../../shared/api/definitions/integration';

const PlaceholderAdditionalFieldMappingSchema = z.object({
  type: z.literal('placeholder'),
  id: ObjectIdSchema,
});

export type PlaceholderAdditionalFieldMapping = z.infer<
  typeof PlaceholderAdditionalFieldMappingSchema
>;
export const isPlaceholderAdditionalFieldMapping = zodTypeguard(
  PlaceholderAdditionalFieldMappingSchema
);

interface IntegrationDetailsState {
  // initialization and server state
  hasInitialized: boolean;
  updateServerState: (serverResponse: CrmIntegrationResponse) => void;
  serverState: CrmIntegrationResponse | null;

  // client state
  managedFieldMappings: ManagedFieldMapping[];
  deleteManagedFieldMapping: (fieldMappingId: ObjectId) => void;
  addManagedFieldMapping: (fieldMapping: ManagedFieldMapping) => void;

  additionalFieldMappings: (
    | AdditionalFieldMapping
    | PlaceholderAdditionalFieldMapping
  )[];
  addAdditionalFieldMapping: (
    fieldMapping: AdditionalFieldMapping | PlaceholderAdditionalFieldMapping
  ) => void;
  updateAdditionalFieldMapping: (fieldMapping: AdditionalFieldMapping) => void;
  deleteAdditionalFieldMapping: (fieldMappingId: ObjectId) => void;

  autoSync: boolean;
  setAutoSync: (autoSync: boolean) => void;
  syncSettings: SyncSettings | undefined;
  setSyncSettings: (syncSettings: SyncSettings) => void;

  getServerConfigFromLocalState: () => Partial<IntegrationConfig>;
  hasChanges: (args?: {includeDraftChanges?: boolean}) => boolean;
  isSaveableFieldMapping: (
    fieldMapping: FieldMapping | PlaceholderAdditionalFieldMapping
  ) => boolean;
}

const createIntegrationDetailsStoreFactory = () =>
  createStore<IntegrationDetailsState>()(
    devtools<IntegrationDetailsState>((set, get) => {
      const getFilteredAdditionalFields = () =>
        get()
          .additionalFieldMappings.filter(isAdditionalFieldMapping)
          .filter(({crmFieldName}) => crmFieldName);

      const getLocalStateFromServerState = (
        serverState: CrmIntegrationResponse
      ) => {
        const config = serverState.config;
        const fieldMappings = config?.fieldMapping ?? [];

        return {
          managedFieldMappings: fieldMappings.filter(isManagedFieldMapping),
          additionalFieldMappings: fieldMappings.filter(
            isAdditionalFieldMapping
          ),
          autoSync: !!config?.autoSync,
          syncSettings: config?.syncSettings,
        };
      };

      const setLocalState = (serverState: CrmIntegrationResponse) => {
        const {
          managedFieldMappings,
          additionalFieldMappings,
          autoSync,
          syncSettings,
        } = getLocalStateFromServerState(serverState);

        set(
          produce<IntegrationDetailsState>((state) => {
            state.managedFieldMappings = managedFieldMappings;
            state.additionalFieldMappings = additionalFieldMappings;
            state.autoSync = autoSync;
            state.syncSettings = syncSettings;
            state.hasInitialized = true;
          })
        );
      };

      return {
        hasInitialized: false,
        updateServerState: (integrationResponse: CrmIntegrationResponse) => {
          // don't blow away unsaved client changes with new server state
          if (
            !get().hasInitialized ||
            !get().hasChanges({includeDraftChanges: true})
          ) {
            setLocalState(integrationResponse);
          }

          // do this last to ensure hasChanges check runs against old server state
          set(
            produce<IntegrationDetailsState>((state) => {
              state.serverState = integrationResponse;
            })
          );
        },
        serverState: null,

        managedFieldMappings: [],
        addManagedFieldMapping: (fieldMapping) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              state.managedFieldMappings.push(fieldMapping);
            })
          ),
        deleteManagedFieldMapping: (fieldMappingId) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              state.managedFieldMappings = state.managedFieldMappings.filter(
                ({id}) => !id.equals(fieldMappingId)
              );
            })
          ),

        additionalFieldMappings: [],
        addAdditionalFieldMapping: (fieldMapping) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              state.additionalFieldMappings.push(fieldMapping);
            })
          ),
        updateAdditionalFieldMapping: (fieldMapping) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              const index = get().additionalFieldMappings.findIndex(({id}) =>
                id.equals(fieldMapping.id)
              );

              if (index === -1) {
                throw new Error('Could not find field to update');
              }

              state.additionalFieldMappings[index] = fieldMapping;
            })
          ),
        deleteAdditionalFieldMapping: (fieldMappingId) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              state.additionalFieldMappings =
                get().additionalFieldMappings.filter(
                  ({id}) => !id.equals(fieldMappingId)
                );
            })
          ),

        autoSync: false,
        setAutoSync: (autoSync) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              state.autoSync = autoSync;
            })
          ),
        syncSettings: undefined,
        setSyncSettings: (syncSettings) =>
          set(
            produce<IntegrationDetailsState>((state) => {
              state.syncSettings = syncSettings;
            })
          ),

        hasChanges: ({
          includeDraftChanges,
        }: {includeDraftChanges?: boolean} = {}) => {
          const serverState = get().serverState;
          if (!get().hasInitialized || !serverState) {
            return false;
          }

          const {
            managedFieldMappings,
            additionalFieldMappings,
            autoSync,
            syncSettings,
          } = getLocalStateFromServerState(serverState);

          const additionalFieldsToCompareAgainst = includeDraftChanges
            ? get().additionalFieldMappings
            : getFilteredAdditionalFields();

          return (
            !_.isEqual(managedFieldMappings, get().managedFieldMappings) ||
            !_.isEqual(
              additionalFieldMappings,
              additionalFieldsToCompareAgainst
            ) ||
            !_.isEqual(syncSettings, get().syncSettings) ||
            autoSync !== get().autoSync
          );
        },

        isSaveableFieldMapping: (
          fieldMapping: FieldMapping | PlaceholderAdditionalFieldMapping
        ) => {
          return (
            !isPlaceholderAdditionalFieldMapping(fieldMapping) &&
            fieldMapping.crmFieldName !== ''
          );
        },

        getServerConfigFromLocalState: () => {
          const fieldMapping = [
            ...get().managedFieldMappings,
            ...getFilteredAdditionalFields(),
          ];

          return {
            fieldMapping,
            autoSync: get().autoSync,
            syncSettings: get().syncSettings,
          };
        },
      };
    })
  );

let integrationDetailsStore: StoreApi<IntegrationDetailsState> | null = null;
export const useIntegrationDetailsState = () => {
  // Only initialize the store once
  if (!integrationDetailsStore) {
    integrationDetailsStore = createIntegrationDetailsStoreFactory();
  }
  const store = useStore(integrationDetailsStore);

  const integration = useCrmIntegrationFromContext();
  const updateIntegrationMutation = useUpdateIntegration();

  const toast = useToast();
  const successToastId = 'successToast';
  const publishChanges = useMutation({
    mutationFn: async () => {
      if (!store.hasInitialized) {
        throw new Error('Cannot publish changes before initializing');
      }

      if (!store.hasChanges()) {
        return;
      }

      return updateIntegrationMutation.mutateAsync({
        type: 'crm',
        integrationType: integration.type,
        config: store.getServerConfigFromLocalState(),
      });
    },
    onSuccess: () => {
      if (toast.isActive(successToastId)) {
        return;
      }
      toast({
        id: successToastId,
        title: 'Successfully updated integration',
        status: 'success',
        duration: 2_000,
      });
    },
  });

  useEffect(() => {
    store.updateServerState(integration);
    // don't add store as dependency because it causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration]);

  return {
    ...store,
    publishChanges,
  };
};
